import { AnalyticsUserEvent } from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import { useEffect } from 'react';
import { FullScreenSpinner } from 'smoothie';

import { IncomeSplitReview } from '../../../../banking/components/income-split/IncomeSplitReview';
import useGetAllBankAccountDetails from '../../../../banking/hooks/useGetAllBankAccountDetails';
import { useViewEvents } from '../../../../events/hooks/useViewEvents';
import { captureException, track } from '../../../../logger/Logger';
import useGetProposedLegacyIncomeSplit from '../../../hooks/useGetProposedLegacyIncomeSplit';
import { useMoneyMapWorkflow } from '../../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../../types/GoWorkflow';

const ProposedIncomeSplitLegacy = () => {
    const { trackViewOnce } = useViewEvents();

    const {
        isLoadingMoneyMap,
        moneyMap,
        hasMoneyMapError,
        moneyMapCompleteness,
        refetchMoneyMapCompleteness,
        trackWorkflowCompletedEvent,
    } = useMoneyMapWorkflow();

    if (
        !isLoadingMoneyMap &&
        !moneyMapCompleteness.incomeSplitStep.isIncluded
    ) {
        router.navigate('/home/roadmap');
    }

    const {
        isLoading: isLoadingIncomeSplit,
        data: incomeSplit,
        isError: isIncomeSplitError,
        error: incomeSplitError,
    } = useGetProposedLegacyIncomeSplit(
        { moneyMapId: moneyMap?.id ?? '' },
        { enabled: !!moneyMap },
    );

    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetails();

    const isLoading =
        isLoadingMoneyMap ||
        isLoadingIncomeSplit ||
        isLoadingBankAccountDetails;

    const hasError = hasMoneyMapError || isIncomeSplitError;

    useEffect(() => {
        if (hasError) {
            incomeSplitError && captureException(incomeSplitError);
            router.navigate('/home/roadmap');
        }
    }, [hasError, incomeSplitError]);

    if (!isLoading && (!moneyMap || !incomeSplit)) {
        return <Redirect href="/home/roadmap" />;
    }

    if (isLoading || !moneyMap || !incomeSplit) {
        return <FullScreenSpinner />;
    }

    trackViewOnce('Roadmap/Go/IncomeSplit');

    const incomeSplitComplete =
        moneyMapCompleteness.incomeSplitStep.isCompleteForMember;

    return (
        <IncomeSplitReview
            incomeSplit={incomeSplit}
            bankAccountDetails={bankAccountDetails}
            context="goButtonFlow"
            incomeSplitComplete={incomeSplitComplete}
            onSubmit={() => {
                track(
                    AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_SUBMIT_INCOME_SPLIT,
                );
            }}
            onIncomeSplitCreated={async () => {
                track(
                    AnalyticsUserEvent.MONEY_MAP_WORKFLOW_INCOME_SPLIT_COMPLETE,
                );
                await refetchMoneyMapCompleteness();
                trackWorkflowCompletedEvent(GoWorkflowStepType.INCOME_SPLIT);
            }}
        />
    );
};

export default ProposedIncomeSplitLegacy;
